<script>
import CancelButton from '@/components/cars/edit/CancelButton.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import uniq from 'lodash/uniq'
import WarrantyOrderDocumentSeatCupra from '@/components/service/WarrantyOrderDocumentSeatCupra.vue'
import WarrantyOrderDocumentSkoda from '@/components/service/WarrantyOrderDocumentSkoda.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import { DateTime } from 'luxon'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import download from 'downloadjs'
import { saveWarrantyWorkDocument } from '@/api/service'

const COMPONENT_MAP = {
  SEAT_CUPRA: 'mia-warranty-order-document-seat-cupra',
  SKODA: 'mia-warranty-order-document-skoda'
}

export default {
  components: {
    'mia-cancel-button': CancelButton,
    'mia-feature-select': FeatureSelect,
    'mia-submit-button': SubmitButton,
    'mia-warranty-order-document-seat-cupra': WarrantyOrderDocumentSeatCupra,
    'mia-warranty-order-document-skoda': WarrantyOrderDocumentSkoda
  },

  mixins: [FormattingHelpers],

  props: {
    savedDocument: {
      type: Object,
      required: true
    },

    timeTrackings: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data: () => ({
    selectedMake: 'SEAT_CUPRA',
    selectedOrderId: null,
    selectedTimeTrackingIds: []
  }),

  computed: {
    availableOrders() {
      const orderIds = uniq(this.timeTrackings.map(timeTracking => timeTracking.item_order_id))

      return orderIds
    },

    makeComponent() {
      return COMPONENT_MAP[this.selectedMake]
    },

    orderTimeTrackings() {
      if (!this.selectedOrderId) {
        return []
      }

      return this.timeTrackings.filter(timeTracking => timeTracking.item_order_id === this.selectedOrderId)
    },

    selectedTimeTrackings() {
      return this.orderTimeTrackings.filter(timeTracking => this.selectedTimeTrackingIds.includes(timeTracking.id))
    }
  },

  created() {
    this.selectedMake = this.savedDocument.item_make
    this.selectedOrderId = this.savedDocument.item_order_id

    const validTimeTrackingIds = this.timeTrackings.map(timeTracking => timeTracking.id)
    this.selectedTimeTrackingIds = this.savedDocument.item_time_tracking_ids.filter(timeTrackingId => validTimeTrackingIds.includes(timeTrackingId))
  },

  methods: {
    async generateDocument() {
      if (this.$wait.is(['generating warranty work document', 'saving warranty work document'])) {
        return
      }

      await this.saveDocument(true)

      this.$wait.start('generating warranty work document')
      this.$refs.makeFormRef.submit((success, result) => {
        this.$wait.end('generating warranty work document')

        if (!success) {
          window.eventBus.$emit('mia-error', {
            error: ['Dokument konnte nicht erzeugt werden']
          })
          return
        }

        let fileName = `${DateTime.local().toFormat('yyyy-MM-dd')}-Beiblatt-zum-Garantieauftrag.pdf`

        if (result.headers['content-disposition']) {
          const matches = result.headers['content-disposition'].match(/filename="(.*?)"/)

          if (matches !== null) {
            fileName = matches[1].trim()
          }
        }

        download(result.data, fileName, result.data.type)

        window.eventBus.$emit('mia-info', {
          info: ['Dokument erfolgreich erstellt']
        })

        this.$emit('document-created', this.selectedTimeTrackingIds)
        this.$emit('closed')
      })
    },

    async saveDocument(silently = false) {
      if (this.$wait.is('saving warranty work document')) {
        return
      }

      this.$wait.start('saving warranty work document')
      const [error] = await saveWarrantyWorkDocument({
        id: this.savedDocument.id,
        item_order_id: this.selectedOrderId || null,
        item_make: this.selectedMake,
        item_time_tracking_ids: this.selectedTimeTrackings.map(timeTracking => timeTracking.id),
        item_data: this.$refs.makeFormRef.getData()
      })
      this.$wait.end('saving warranty work document')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Dokument konnte nicht gespeichert werden']
        })
        return
      }

      window.eventBus.$emit('mia-info', {
        info: ['Dokument erfolgreich gespeichert']
      })

      if (silently) {
        this.$emit('saved')
        return
      }

      this.$emit('saved')
      this.$emit('closed')
    },

    onTimeTrackingSelectionChange(timeTracking, event) {
      this.selectedTimeTrackingIds = [...this.selectedTimeTrackingIds].filter(timeTrackingId => timeTrackingId !== timeTracking.id)

      if (event.target.checked) {
        this.selectedTimeTrackingIds = [...this.selectedTimeTrackingIds, timeTracking.id]
      }
    },

    onMakeComponentMounted() {
      this.$refs.makeFormRef.restore(this.savedDocument.item_data)
    }
  }
}
</script>

<template>
  <mia-modal
    id="edit-warranty-order-document-modal"
    classes="v--modal flex flex-col"
    :click-to-close="false"
    width="75%"
    height="auto"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <mia-modal-header>
      <h3 class="py-0 my-0">
        Beiblatt zum Garantie-/Kulanzauftrag bearbeiten
      </h3>
    </mia-modal-header>

    <div class="p-4">
      <div class="grid grid-cols-3 gap-6">
        <mia-feature-select
          v-model="selectedMake"
          label="Hersteller"
        >
          <template #options>
            <option value="SEAT_CUPRA">
              Seat/Cupra
            </option>
            <option value="SKODA">
              Skoda
            </option>
          </template>
        </mia-feature-select>

        <mia-feature-select
          v-model="selectedOrderId"
          label="Auftragsnummer"
        >
          <template #options>
            <option
              v-for="oId in availableOrders"
              :key="oId"
              :value="oId"
            >
              {{ oId }}
            </option>
          </template>
        </mia-feature-select>
      </div>

      <div
        v-if="selectedOrderId"
        class="mt-4"
      >
        <table class="w-full rounded border border-gray-200">
          <thead>
            <tr>
              <th class="p-1 bg-gray-200">
&nbsp;
              </th>
              <th class="p-1 bg-gray-200">
                Datum
              </th>
              <th class="p-1 bg-gray-200">
                AW verkauft
              </th>
              <th class="p-1 bg-gray-200">
                Kommentar
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(timeTracking, index) in orderTimeTrackings"
              :key="timeTracking.id"
            >
              <td
                class="p-1"
                :class="{ 'bg-gray-100': index % 2 === 0 }"
              >
                <mia-checkbox
                  :checked="selectedTimeTrackingIds.includes(timeTracking.id)"
                  @change="onTimeTrackingSelectionChange(timeTracking, $event)"
                />
              </td>
              <td
                class="p-1"
                :class="{ 'bg-gray-100': index % 2 === 0 }"
              >
                {{ formatAsDate(timeTracking.item_date, 'sql') }}
              </td>
              <td
                class="p-1"
                :class="{ 'bg-gray-100': index % 2 === 0 }"
              >
                {{ timeTracking.item_hours_sold }}
              </td>
              <td
                class="p-1"
                :class="{ 'bg-gray-100': index % 2 === 0 }"
              >
                {{ timeTracking.item_comment }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-4">
        <component
          :is="makeComponent"
          ref="makeFormRef"
          :make="selectedMake"
          :order-id="selectedOrderId"
          :time-trackings="selectedTimeTrackings"
          @mounted="onMakeComponentMounted"
        />
      </div>
    </div>

    <mia-modal-footer class="flex items-center justify-end print:hidden">
      <div class="flex">
        <mia-cancel-button
          label="Schließen"
          type="button"
          :disabled="$wait.is(['generating warranty work document', 'saving warranty work document'])"
          size="small"
          @click="$emit('closed')"
        />

        <mia-submit-button
          class="ml-12"
          label="Speichern"
          type="button"
          :is-loading="$wait.is('saving warranty work document')"
          size="small"
          @click="saveDocument"
        />

        <mia-submit-button
          class="ml-3"
          label="Speichern und PDF erstellen"
          type="button"
          :is-loading="$wait.is('generating warranty work document')"
          size="small"
          @click="generateDocument"
        />
      </div>
    </mia-modal-footer>
  </mia-modal>
</template>
